import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
  requiredPlans?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSubscription = false,
  requiredPlans = [],
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (requireSubscription) {
      const hasActiveSubscription = user?.subscription?.status === 'active';

      if (!hasActiveSubscription) {
        navigate('/dashboard/subscription');
        return;
      }

      if (requiredPlans.length > 0) {
        const userPlan = user?.subscription?.plan;
        if (!userPlan || !requiredPlans.includes(userPlan)) {
          navigate('/dashboard/subscription');
        }
      }
    }
  }, [
    loading,
    isAuthenticated,
    user,
    requireSubscription,
    requiredPlans,
    navigate,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (
    requireSubscription &&
    user?.subscription?.status !== 'active'
  ) {
    return null;
  }

  return <>{children}</>;
};
